/**
 * Agent export envelope schema.
 *
 * Describes the wire format produced by the export modal and consumed by the
 * import wizard. Validated on both the client (here) and the server in
 * `backend/src/services/agentExportImportService.ts` — keep both files in
 * lock-step. The on-the-wire shape is independent of the in-memory `Agent`
 * type so we can evolve the agent model without breaking export files.
 */
import { z } from 'zod';

export const EXPORT_SCHEMA_ID = 'stellar-chat/agent-export/v1';
export const REDACTED_SENTINEL = '__REDACTED__';
export const SUPPORTED_EXPORT_VERSION = 1;

const reasoningConfigSchema = z
  .object({
    supportsReasoning: z.boolean(),
    offEffort: z.enum(['minimal', 'low', 'medium', 'high']).optional(),
    onEffort: z.enum(['minimal', 'low', 'medium', 'high']).optional(),
    defaultEnabled: z.boolean().optional(),
  })
  .strict()
  .optional();

const azureAiAgentConfig = z
  .object({
    endpoint: z.string(),
    apiKey: z.string(),
    deploymentId: z.string(),
    apiVersion: z.string().optional(),
    reasoningConfig: reasoningConfigSchema,
    supportsVision: z.boolean().optional(),
    contextWindowTokens: z.number().int().positive().optional(),
    contextOverflowStrategy: z.enum(['trim', 'summarize']).optional(),
    summaryModel: z
      .object({
        deployment: z.string(),
        endpoint: z.string().optional(),
        apiKey: z.string().optional(),
        apiVersion: z.string().optional(),
      })
      .strict()
      .optional(),
  })
  .strict();

const copilotConfig = z
  .object({
    endpoint: z.string(),
    apiKey: z.string(),
    model: z.string().optional(),
  })
  .strict();

const mcpConfig = z
  .object({
    command: z.string(),
    args: z.array(z.string()).optional(),
    env: z.record(z.string(), z.string()).optional(),
    cwd: z.string().optional(),
  })
  .strict();

const agentConfigDiscriminated = z.discriminatedUnion('framework', [
  z
    .object({
      framework: z.literal('azure-ai-agent'),
      config: azureAiAgentConfig,
    })
    .strict(),
  z
    .object({
      framework: z.literal('copilot'),
      config: copilotConfig,
    })
    .strict(),
  z
    .object({
      framework: z.literal('mcp'),
      config: mcpConfig,
    })
    .strict(),
]);

const aiSearchConfigSchema = z.record(z.string(), z.unknown()).optional();
const webSearchConfigSchema = z.record(z.string(), z.unknown()).optional();
const welcomeMessageSchema = z
  .object({
    useCustomMessage: z.boolean(),
    title: z.string(),
    subtitle: z.string(),
  })
  .strict()
  .optional();

const suggestedPromptSchema = z
  .object({
    title: z.string(),
    prompt: z.string(),
    icon: z.string().optional(),
    category: z.string().optional(),
  })
  .strict();

const agentSchema = z
  .object({
    name: z.string().min(1),
    description: z.string(),
    icon: z.string().optional(),
    framework: z.enum(['mcp', 'copilot', 'azure-ai-agent']),
    capabilities: z.array(z.string()),
    config: agentConfigDiscriminated,
    metadata: z.record(z.string(), z.unknown()).optional(),
    systemPrompt: z.string().optional(),
    suggestedPrompts: z.array(suggestedPromptSchema).optional(),
    welcomeMessage: welcomeMessageSchema,
    webSearchConfig: webSearchConfigSchema,
    aiSearchConfig: aiSearchConfigSchema,
  })
  .strict()
  .superRefine((agent, ctx) => {
    if (agent.framework !== agent.config.framework) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['config', 'framework'],
        message: `agent.config.framework must equal agent.framework ("${agent.framework}")`,
      });
    }
  });

export const exportEnvelopeSchema = z
  .object({
    $schema: z.literal(EXPORT_SCHEMA_ID),
    exportVersion: z.literal(SUPPORTED_EXPORT_VERSION),
    exportedAt: z.string(),
    exportedFrom: z
      .object({
        appVersion: z.string(),
        environment: z.string(),
        exportedBy: z.string(),
      })
      .strict(),
    secretsIncluded: z.boolean(),
    // Human-readable banner. Populated only when secretsIncluded === true.
    _warning: z.string().optional(),
    agent: agentSchema,
  })
  .strict();

export type ExportEnvelope = z.infer<typeof exportEnvelopeSchema>;
export type ExportableAgent = z.infer<typeof agentSchema>;
